/**
 * TemplateWizard — sequential step-by-step workflow-template builder.
 * Steps:
 *   1. Basics (name, entity_type, description, is_default)
 *   2. Add stages ONE at a time, live preview via WorkflowDiagram
 *   3. Review & Save
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Check, Plus, Trash2, Users, GitBranch, Save } from 'lucide-react';
import WorkflowDiagram from '@/components/dms/WorkflowDiagram';
import api from '@/lib/api';

const emptyStage = () => ({
  name: '', mode: 'sequential',
  reviewers: [{ role: 'CM', email: '', sla_days: 5 }],
  allowed_actions: ['APPROVE', 'REVISE', 'REJECT'],
  escalation: { enabled: true, grace_days: 2, escalate_to: 'next_stage' },
});

export default function TemplateWizard({ projectId, initial, onSaved, onCancel }) {
  const [step, setStep] = useState(1);
  const [basics, setBasics] = useState({
    id:          initial?.id,
    name:        initial?.name || '',
    description: initial?.description || '',
    entity_type: initial?.entity_type || 'submittal',
    is_default:  !!initial?.is_default,
  });
  const [stages, setStages]           = useState(initial?.stages ? cloneStages(initial.stages) : []);
  const [editingStage, setEditingStage] = useState(null);   // draft stage for step 2
  const [editingIdx, setEditingIdx]     = useState(null);   // null=add-new, number=edit-in-place

  const canNext1 = basics.name.trim();
  const canFinish = stages.length > 0 && stages.every(validStage);

  const startNewStage  = () => { setEditingStage(emptyStage()); setEditingIdx(null); };
  const editExisting   = (i) => { setEditingStage(cloneStage(stages[i])); setEditingIdx(i); };
  const cancelStageEdit = () => { setEditingStage(null); setEditingIdx(null); };
  const commitStage    = () => {
    if (!validStage(editingStage)) { toast.error('Stage needs a name and at least one reviewer.'); return; }
    if (editingIdx === null) setStages(prev => [...prev, editingStage]);
    else                     setStages(prev => prev.map((s, i) => i === editingIdx ? editingStage : s));
    setEditingStage(null); setEditingIdx(null);
  };
  const removeStage = (i) => setStages(prev => prev.filter((_, idx) => idx !== i));
  const moveStage   = (i, dir) => setStages(prev => {
    const j = i + dir;
    if (j < 0 || j >= prev.length) return prev;
    const c = [...prev]; [c[i], c[j]] = [c[j], c[i]]; return c;
  });

  const save = async () => {
    try {
      const body = { ...basics, stages };
      if (basics.id) {
        await api.put(`/dms/projects/${projectId}/workflows/templates/${basics.id}`, body);
        toast.success('Template updated');
      } else {
        await api.post(`/dms/projects/${projectId}/workflows/templates`, body);
        toast.success('Template created');
      }
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  return (
    <div className="space-y-4">
      <Stepper step={step} labels={['Basics', 'Stages', 'Review']} />

      {step === 1 && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Template name*</Label>
              <Input autoFocus value={basics.name} onChange={(e) => setBasics({ ...basics, name: e.target.value })} placeholder="e.g. Shop Drawing Review — Parallel CM + Arch" data-testid="wiz-name" />
            </div>
            <div>
              <Label className="text-xs">Applies to</Label>
              <Select value={basics.entity_type} onValueChange={(v) => setBasics({ ...basics, entity_type: v })}>
                <SelectTrigger className="h-8" data-testid="wiz-entity"><SelectValue /></SelectTrigger>
                <SelectContent>{['submittal', 'transmittal', 'rfi', 'review'].map(v => <SelectItem key={v} value={v}>{v.toUpperCase()}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea rows={2} value={basics.description} onChange={(e) => setBasics({ ...basics, description: e.target.value })} placeholder="What is this workflow for?" />
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={basics.is_default} onChange={(e) => setBasics({ ...basics, is_default: e.target.checked })} data-testid="wiz-default" />
            Set as default for {basics.entity_type}s (auto-attached on new entities of this type)
          </label>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <div className="border-l-4 border-l-sky-500 bg-slate-50 dark:bg-slate-800/40 rounded-lg p-3">
            <div className="text-[10px] uppercase text-slate-500 mb-2">Live preview</div>
            <WorkflowDiagram stages={stages} />
          </div>

          <div className="border border-slate-200 dark:border-slate-700/40 rounded-lg divide-y divide-slate-200 dark:divide-slate-700/40">
            {stages.map((s, i) => (
              <div key={i} className="flex items-center gap-2 p-2 text-xs" data-testid={`wiz-stage-row-${i}`}>
                <span className="w-6 h-6 rounded-full bg-sky-500/20 text-sky-600 dark:text-sky-300 flex items-center justify-center text-[10px] font-bold">{i + 1}</span>
                <span className="flex-1 font-semibold">{s.name}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600">{s.mode === 'parallel' ? <span className="flex items-center gap-1"><Users className="w-2.5 h-2.5" /> parallel</span> : 'sequential'}</span>
                <span className="text-slate-500 text-[10px]">{s.reviewers.length} reviewer{s.reviewers.length === 1 ? '' : 's'}</span>
                <button onClick={() => moveStage(i, -1)} disabled={i === 0} className="text-slate-500 hover:text-sky-500 disabled:opacity-30" title="Move up">↑</button>
                <button onClick={() => moveStage(i, +1)} disabled={i === stages.length - 1} className="text-slate-500 hover:text-sky-500 disabled:opacity-30" title="Move down">↓</button>
                <button onClick={() => editExisting(i)} className="text-sky-500 hover:underline">Edit</button>
                <button onClick={() => removeStage(i)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3 h-3" /></button>
              </div>
            ))}
            {stages.length === 0 && <div className="text-xs text-slate-500 p-4 text-center">No stages yet — add the first one below.</div>}
          </div>

          {!editingStage ? (
            <Button size="sm" variant="outline" onClick={startNewStage} data-testid="wiz-add-stage">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Stage
            </Button>
          ) : (
            <StageEditor
              value={editingStage}
              onChange={setEditingStage}
              onCancel={cancelStageEdit}
              onCommit={commitStage}
              isEdit={editingIdx !== null}
            />
          )}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3">
          <div className="border border-slate-200 dark:border-slate-700/40 rounded-lg p-3 space-y-2">
            <div><b>Name:</b> {basics.name} {basics.is_default && <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-600">DEFAULT</span>}</div>
            <div><b>Applies to:</b> {basics.entity_type.toUpperCase()}</div>
            {basics.description && <div><b>Description:</b> {basics.description}</div>}
            <div><b>Stages ({stages.length}):</b></div>
            <WorkflowDiagram stages={stages} />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700/40 pt-3">
        <div>
          <Button variant="outline" onClick={onCancel} data-testid="wiz-cancel">Cancel</Button>
        </div>
        <div className="flex items-center gap-2">
          {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="wiz-back"><ChevronLeft className="w-3.5 h-3.5 mr-1" /> Back</Button>}
          {step < 3 && <Button className="bg-[#1E4E8C] text-white" onClick={() => setStep(step + 1)} disabled={step === 1 ? !canNext1 : stages.length === 0} data-testid="wiz-next">Next <ChevronRight className="w-3.5 h-3.5 ml-1" /></Button>}
          {step === 3 && <Button className="bg-emerald-600 text-white" onClick={save} disabled={!canFinish} data-testid="wiz-save"><Save className="w-3.5 h-3.5 mr-1" /> {basics.id ? 'Update' : 'Create'} Template</Button>}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
function Stepper({ step, labels }) {
  return (
    <div className="flex items-center gap-2" data-testid="wiz-stepper">
      {labels.map((l, i) => {
        const n = i + 1;
        const done = n < step, cur = n === step;
        return (
          <React.Fragment key={l}>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${cur ? 'bg-sky-500/20 text-sky-700 dark:text-sky-300 font-semibold' : done ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${cur ? 'bg-sky-500 text-white' : done ? 'bg-emerald-500 text-white' : 'bg-slate-300 dark:bg-slate-700 text-slate-500'}`}>
                {done ? <Check className="w-3 h-3" /> : n}
              </span>
              {l}
            </div>
            {i < labels.length - 1 && <div className="flex-1 h-px bg-slate-300 dark:bg-slate-700" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function StageEditor({ value, onChange, onCancel, onCommit, isEdit }) {
  const patch = (p) => onChange({ ...value, ...p });
  const setReviewer = (i, p) => onChange({ ...value, reviewers: value.reviewers.map((r, idx) => idx === i ? { ...r, ...p } : r) });
  const addReviewer = () => onChange({ ...value, reviewers: [...value.reviewers, { role: '', email: '', sla_days: 5 }] });
  const removeReviewer = (i) => onChange({ ...value, reviewers: value.reviewers.filter((_, idx) => idx !== i) });
  const toggleAction = (a) => onChange({ ...value, allowed_actions: value.allowed_actions.includes(a) ? value.allowed_actions.filter(x => x !== a) : [...value.allowed_actions, a] });

  return (
    <div className="border-2 border-sky-500/40 rounded-lg p-3 bg-sky-50/30 dark:bg-sky-500/5 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-sky-700 dark:text-sky-300 flex items-center gap-1"><GitBranch className="w-3.5 h-3.5" /> {isEdit ? 'Edit' : 'New'} stage</div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">Stage name*</Label>
          <Input autoFocus value={value.name} onChange={(e) => patch({ name: e.target.value })} placeholder="Contractor Prep" data-testid="wiz-stage-name" />
        </div>
        <div>
          <Label className="text-xs">Approval mode</Label>
          <Select value={value.mode} onValueChange={(v) => patch({ mode: v })}>
            <SelectTrigger className="h-8" data-testid="wiz-stage-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="sequential">Sequential (any 1)</SelectItem>
              <SelectItem value="parallel">Parallel (ALL approve)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="text-xs">Reviewers ({value.reviewers.length})</Label>
        {value.reviewers.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-1 mb-1">
            <Input placeholder="Role (CM, PM, …)" value={r.role} onChange={(e) => setReviewer(i, { role: e.target.value })} className="col-span-4 h-7 text-xs" data-testid={`wiz-stage-role-${i}`} />
            <Input placeholder="OR email"          value={r.email} onChange={(e) => setReviewer(i, { email: e.target.value })} className="col-span-5 h-7 text-xs" />
            <Input type="number" placeholder="SLA days" value={r.sla_days} onChange={(e) => setReviewer(i, { sla_days: +e.target.value })} className="col-span-2 h-7 text-xs" data-testid={`wiz-stage-sla-${i}`} />
            <Button size="sm" variant="ghost" className="col-span-1 h-7 text-red-500 p-0" onClick={() => removeReviewer(i)}><Trash2 className="w-3 h-3" /></Button>
          </div>
        ))}
        <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={addReviewer} data-testid="wiz-stage-add-reviewer">+ Add Reviewer</Button>
      </div>

      <div>
        <Label className="text-xs">Allowed actions</Label>
        <div className="flex flex-wrap gap-1 mt-1">
          {['APPROVE', 'APPROVED_AS_NOTED', 'REVISE', 'REJECT', 'FORWARD'].map(a => (
            <label key={a} className={`text-[10px] px-2 py-0.5 rounded-full cursor-pointer border ${value.allowed_actions.includes(a) ? 'bg-sky-500/20 border-sky-500 text-sky-700 dark:text-sky-300' : 'border-slate-300 dark:border-slate-600 text-slate-500'}`}>
              <input type="checkbox" className="hidden" checked={value.allowed_actions.includes(a)} onChange={() => toggleAction(a)} />
              {a.replace(/_/g, ' ')}
            </label>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-2 text-xs text-slate-500">
        <input type="checkbox" checked={value.escalation?.enabled ?? false} onChange={(e) => patch({ escalation: { ...(value.escalation || {}), enabled: e.target.checked, grace_days: value.escalation?.grace_days ?? 2, escalate_to: 'next_stage' } })} />
        Auto-escalate to next stage after SLA + <Input type="number" value={value.escalation?.grace_days ?? 2} onChange={(e) => patch({ escalation: { ...(value.escalation || {}), grace_days: +e.target.value } })} className="h-6 w-16 inline-block" /> grace days
      </label>

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button size="sm" className="bg-sky-600 text-white" onClick={onCommit} data-testid="wiz-stage-commit">
          <Check className="w-3.5 h-3.5 mr-1" /> {isEdit ? 'Save Stage' : 'Add Stage'}
        </Button>
      </div>
    </div>
  );
}

// Helpers
function validStage(s) { return s && s.name?.trim() && s.reviewers?.length > 0; }
function cloneStages(stages) { return stages.map(cloneStage); }
function cloneStage(s) {
  return {
    name: s.name || '',
    mode: s.mode || 'sequential',
    reviewers: (s.reviewers || []).map(r => ({ role: r.role || '', email: r.email || '', sla_days: r.sla_days || 5 })),
    allowed_actions: s.allowed_actions ? [...s.allowed_actions] : ['APPROVE', 'REVISE', 'REJECT'],
    escalation: s.escalation ? { ...s.escalation } : { enabled: false, grace_days: 2, escalate_to: 'next_stage' },
  };
}
